import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

// Redux slices and actions
import { setSelectedGroup, clearSelectedGroup } from "src/slices/userGroupSlice";

// Custom hooks
import { useUserGroupsData } from "src/hooks/useUserGroupsData";
import { useDialogBox } from "src/hooks/useDialogBox";

// Layout/UI
import { Box, Button } from "@metrea-ui/v2";
import { PageHeader } from "../SiteLayout/Header";
import { PageFooter } from "../SiteLayout/Footer";
import UserGroupsList from "./UserGroupsList";
import UserGroupDetails from "./EditUserGroup";

// Utilities
import { debugLog, errorLog } from "src/utils/debugUtils";
// Constants
import { initialUserGroupState } from "src/constants/initialStates";

const UserGroups = () => {
	const renderCount = useRef(0);
	renderCount.current++;

	const dispatch = useDispatch();
	const { withError } = useDialogBox();

	// --- Redux Selectors ---
	const itemList = useSelector((state) => state.userGroups.list);
	const selectedGroup = useSelector((state) => state.userGroups.selectedGroup);
	const showList = useSelector((state) => !state.userGroups.selectedGroup); // Derived from Redux

	// --- Redux Status/Error : For error and loading overlays ---
	const retrieveGroupsError = useSelector((state) => state.userGroups.error.retrieve);
	const retrieveGroupDetailError = useSelector((state) => state.userGroups.error.retrieveOne);

	// --- Local State ---
	const [pageM1, setPageM1] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	// Custom hooks
	const { fetchUserGroups, fetchUserGroupById } = useUserGroupsData();

	// --- Effect: initial data load
	useEffect(() => {
		debugLog(2, "[UserGroups][FLOW] mounted", {});
		if (selectedGroup) {
			dispatch(clearSelectedGroup());
		}

		debugLog(2, "[UserGroups][FLOW] fetchUserGroups started", {});
		fetchUserGroups();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// --- Effect: show Error dialog
	useEffect(() => {
		if (retrieveGroupsError) {
			errorLog(2, "[UserGroups][FLOW] fetchUserGroups failed", { error: retrieveGroupsError });
			withError(retrieveGroupsError, () => fetchUserGroups(), {
				fallbackMessage: "Retrieve Groups Error",
			});
		}
	}, [retrieveGroupsError]);

	useEffect(() => {
		if (retrieveGroupDetailError) {
			errorLog(2, "[UserGroups][FLOW] fetchUserGroupById failed", {
				error: retrieveGroupDetailError,
			});
			// Opening an individual group can fail after the list already loaded, so
			// keep detail-fetch errors visible instead of only handling list failures.
			withError(retrieveGroupDetailError, null, {
				fallbackMessage: "Retrieve User Group Error",
			});
		}
	}, [retrieveGroupDetailError]);

	// --- Handlers ---
	const handleReloadList = () => {
		debugLog(3, "[UserGroups][ACTION] handleReloadList", {});
		dispatch(clearSelectedGroup());
		debugLog(2, "[UserGroups][FLOW] fetchUserGroups started", {});
		fetchUserGroups();
	};

	const selectUserGroupHandler = (id) => {
		debugLog(3, "[UserGroups][ACTION] selectUserGroupHandler", { id: id });
		debugLog(2, "[UserGroups][FLOW] fetchUserGroupById started", { id: id });

		fetchUserGroupById(id);
	};

	const clickNewHandler = (event) => {
		debugLog(3, "[UserGroups][ACTION] clickNewHandler", {});

		dispatch(setSelectedGroup({ ...initialUserGroupState }));
	};

	const changePageM1Handler = (newPage) => {
		//To handle page change from Pagination (MetreaUI)
		setPageM1(newPage);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[UserGroups][ACTION] changeRowsPerPageHandler", { option: option });

		setRowsPerPage(option);
		setPageM1(1);
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[UserGroups][FLOW] render", {
		renderCount: renderCount.current,
		selectedGroup: selectedGroup,
	});

	return (
		<>
			<PageHeader pageTitle="User Group Management" actions={true}>
				{showList && (
					<>
						<Button style={{ padding: "10px" }} onClick={clickNewHandler}>
							Add New
						</Button>
					</>
				)}
			</PageHeader>

			<Box className="pageContent">
				{showList ? (
					<UserGroupsList
						items={itemList}
						pageM1={pageM1}
						setPageM1={changePageM1Handler}
						rowsPerPage={rowsPerPage}
						setRowsPerPage={changeRowsPerPageHandler}
						onSelectUserGroup={selectUserGroupHandler}
					/>
				) : (
					<UserGroupDetails
						item={selectedGroup}
						mode={selectedGroup.id >= 0 ? "edit" : "read"}
						onReloadList={handleReloadList}
					/>
				)}
				<PageFooter />
			</Box>
		</>
	);
};

export default UserGroups;
